import { useSyncExternalStore } from 'react';
import type { RouterAuthContext } from '@/utils/router-auth';
import { loadAuthContext } from '@/utils/router-auth';

/**
 * Store pour l'authentification avec système d'abonnement
 * Utilise le pattern observer pour notifier les composants React
 */
class AuthStore {
  private listeners = new Set<() => void>();
  private snapshot: RouterAuthContext = loadAuthContext();

  /**
   * Abonne un listener aux changements d'auth
   * Retourne une fonction de désabonnement
   */
  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  /**
   * Retourne le snapshot actuel de l'auth
   * Cached pour éviter les re-renders inutiles
   */
  getSnapshot = (): RouterAuthContext => {
    return this.snapshot;
  };

  /**
   * Notifie tous les listeners qu'un changement a eu lieu
   * Appelé par saveAuthContext() et clearAuthContext()
   */
  notify = () => {
    this.snapshot = loadAuthContext();
    this.listeners.forEach((listener) => listener());
  };
}

// Instance singleton du store
const authStore = new AuthStore();

/**
 * Hook React qui s'abonne au store d'authentification
 *
 * Utilise useSyncExternalStore (React 18+) pour :
 * - Synchroniser avec localStorage (store externe)
 * - Updates automatiques quand l'auth change
 * - Type-safe et performant
 * - Pas de useEffect, pas de state local
 *
 * @returns RouterAuthContext - État d'authentification actuel
 *
 * @example
 * function MyComponent() {
 *   const auth = useAuthContext();
 *   return <div>{auth.isAuthenticated ? 'Logged in' : 'Logged out'}</div>;
 * }
 */
export function useAuthContext(): RouterAuthContext {
  return useSyncExternalStore(
    authStore.subscribe,
    authStore.getSnapshot,
    authStore.getSnapshot // SSR snapshot (identique pour notre cas)
  );
}

/**
 * Notifie le store qu'un changement d'auth a eu lieu
 * Déclenche le re-render de tous les composants abonnés via useAuthContext()
 *
 * Cette fonction est appelée par saveAuthContext() et clearAuthContext()
 * dans router-auth.ts
 */
export function notifyAuthChange(): void {
  authStore.notify();
}
